import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  AssetState,
  CommercialCheckout,
  GotoCommercialCheckout,
  GotoPurchaseInformation,
  GotoSubscriptionManagement,
  MSG_STARTUP_FLOW_PURCHASE_NOT_ALLOWED,
  MSG_STARTUP_PURCHASE_WITH_ASSETS,
  MSG_SWITCH_FLOW_FROM_PURCHASE_TO_PURCHINFO,
  MSG_SWITCH_FLOW_FROM_PURCHASE_TO_SUBMAN,
  ProductState,
  RefreshCheckoutMessageParams
} from '@platform-frontend/checkout';
import { PlannedError, ShowErrorNotification, ShowInfoNotification, throwAndHandleError, TranslationId } from '@platform-frontend/util';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { WebCheckoutFlowStartupService } from '../commercial-common/web-checkout-flow-startup.service';

export const CommercialCheckoutStartupGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const webCheckoutFlowStartupService = inject(WebCheckoutFlowStartupService);
  const store = inject(Store);

  webCheckoutFlowStartupService
    .getStartupObservable<CommercialCheckout>(
      route,
      (startParams: RefreshCheckoutMessageParams) => ({
        type: 'COMMERCIAL_CHECKOUT',
        startParams
      }),
      (startParams: RefreshCheckoutMessageParams, startParamsWithAssetContractId: RefreshCheckoutMessageParams) =>
        switchFlowIfNecessary(store, startParams, startParamsWithAssetContractId),
      true
    )
    .pipe(switchMap(() => informAboutExistingAssets(store)))
    .subscribe();
  return true;
};

const switchFlowIfNecessary: (
  store: Store,
  startParams: RefreshCheckoutMessageParams,
  startParamsWithAssetContractId: RefreshCheckoutMessageParams
) => Observable<boolean> = (store: Store, startParams: RefreshCheckoutMessageParams, startParamsWithAssetContractId: RefreshCheckoutMessageParams) => {
  if (store.selectSnapshot(AssetState.isPurchaseAllowed)) {
    if (
      startParams.contractId === null &&
      startParamsWithAssetContractId.contractId !== startParams.contractId &&
      !store.selectSnapshot(ProductState.isMultipleCheckoutAllowed)
    ) {
      return store.dispatch(new GotoCommercialCheckout(startParamsWithAssetContractId, { clearNotifications: false })).pipe(map(() => true));
    }
    return of(false);
  } else if (store.selectSnapshot(AssetState.isOngoingSubscription)) {
    return switchFlow(store, () => store.dispatch(new GotoSubscriptionManagement(startParamsWithAssetContractId)), MSG_SWITCH_FLOW_FROM_PURCHASE_TO_SUBMAN);
  } else if (store.selectSnapshot(AssetState.isOneTimePurchase)) {
    return switchFlow(store, () => store.dispatch(new GotoPurchaseInformation(startParamsWithAssetContractId)), MSG_SWITCH_FLOW_FROM_PURCHASE_TO_PURCHINFO);
  } else {
    return throwAndHandleError(new ShowErrorNotification(MSG_STARTUP_FLOW_PURCHASE_NOT_ALLOWED), new PlannedError('PURCHASE_NOT_ALLOWED'));
  }
};

const switchFlow: (store: Store, dispatchGotoAction: () => Observable<any>, infoMessage: TranslationId) => Observable<true> = (
  store: Store,
  dispatchGotoAction: () => Observable<any>,
  infoMessage: TranslationId
) => {
  return dispatchGotoAction().pipe(
    switchMap(() => store.dispatch(new ShowInfoNotification(infoMessage))),
    map(() => true)
  );
};

const informAboutExistingAssets: (store: Store) => Observable<unknown> = (store: Store) => {
  const quantitySum = store.selectSnapshot(AssetState.quantitySum);
  const productName = store.selectSnapshot(ProductState.productName);
  const productCategory = store.selectSnapshot(ProductState.productCategory);

  return quantitySum > 0 && productCategory !== 'TRAVEL_TICKET'
    ? store.dispatch(new ShowInfoNotification(MSG_STARTUP_PURCHASE_WITH_ASSETS(quantitySum, productName ?? '')))
    : of(null);
};
